import React from 'react';
import API_BASE_URL from '../../config';
import { renderAnswer } from '../chat/answerParts';
import ContentGaps from './ContentGaps';

const cell = { padding: '8px 10px', borderBottom: '1px solid #eee', textAlign: 'left', fontSize: 13 };
const percent = (n, total) => total ? `${Math.round(n / total * 100)}%` : '—';
const authHeaders = (adminKey, customerKey) => adminKey ? { 'X-Admin-Key': adminKey } : { 'X-Customer-Key': customerKey };
const audienceLabel = (audiences, id) => id === '*' || !id ? 'No audience selected' : audiences.find((a) => a.id === id)?.label || id;

export const QUESTIONS_PAGE = 15;
// Longer answers open on their first lines, with a toggle for the rest.
const LONG_ANSWER_CHARS = 280;
const clamped = { display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' };
const linkButton = { background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 12, color: '#1e5eb8', cursor: 'pointer', textDecoration: 'underline' };

/** When a question was asked, in the viewer's time zone. */
export const askedAt = (ts) => new Date(ts * 1000).toLocaleString(undefined,
  { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

// What happened to each question, in the words an agency uses. The groups
// match the server's (app/analytics/aggregate.py OUTCOME_GATES), which also
// filters the question list by them.
export const OUTCOMES = [
  { key: 'answered', label: 'Answered', hint: 'Answered from your published materials' },
  { key: 'not_found', label: 'Not found', hint: 'Not in your published materials' },
  { key: 'declined', label: 'Declined', hint: 'Personal advice, personal information, or another agency' },
  { key: 'greeting', label: 'Greetings', hint: 'Hello or thank you' },
  { key: 'clarify', label: 'Asked to clarify', hint: 'Asked which place or stop the rider meant' },
  { key: 'guided', label: 'Handled by guidance', hint: 'Answered with your own guidance message' },
  { key: 'other', label: 'Other', hint: 'Daily limit reached or the request failed' },
];
const OUTCOME_GATES = {
  answered: ['answered'], not_found: ['L1_evidence', 'L2_sentinel'],
  declined: ['L0_advice', 'L0_pii', 'L0_other_agency'], greeting: ['L0_smalltalk'],
  clarify: ['transit_clarify'], guided: ['guidance'], other: ['budget_exceeded', 'error'],
};
export const GATE_LABELS = {
  answered: 'Answered',
  L0_smalltalk: 'Greeting',
  L0_advice: 'Declined: personal advice',
  L0_pii: 'Declined: personal information',
  L0_other_agency: 'Declined: about another agency',
  L1_evidence: 'Not found in published materials',
  L2_sentinel: 'Not found in published materials',
  transit_clarify: 'Asked which place or stop',
  guidance: 'Handled by guidance',
  budget_exceeded: 'Daily limit reached',
  error: 'Error',
};
export const outcomeLabel = (gate) => GATE_LABELS[gate || 'answered'] || 'Other';

/** Counts per outcome: the server's by_outcome, or derived from by_gate for
 *  a server that predates it. */
export function outcomeCounts(totals = {}) {
  if (totals.by_outcome) return totals.by_outcome;
  const counts = Object.fromEntries(OUTCOMES.map((o) => [o.key, 0]));
  for (const [gate, n] of Object.entries(totals.by_gate || {})) {
    const key = Object.keys(OUTCOME_GATES).find((k) => OUTCOME_GATES[k].includes(gate)) || 'other';
    counts[key] += n;
  }
  return counts;
}

export function languageName(code) {
  try {
    return new Intl.DisplayNames(['en'], { type: 'language' }).of(code) || code;
  } catch {
    return code;
  }
}

const card = (active) => ({
  flex: '1 1 150px', textAlign: 'left', padding: '12px 14px', borderRadius: 8, cursor: 'pointer', font: 'inherit',
  border: `1px solid ${active ? '#1e5eb8' : '#e3e3e3'}`, background: active ? '#f2f7ff' : '#fff',
});

/** The dashboard's top row: one card per outcome. A card opens the tab of
 *  questions it counts. */
export function SummaryCards({ totals = {}, selected, onSelect }) {
  const counts = outcomeCounts(totals);
  const total = totals.queries || 0;
  const shown = OUTCOMES.filter((o) => o.key !== 'other' || counts.other);
  return <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '12px 0' }}>
    <button type="button" style={card(selected === 'all')} aria-pressed={selected === 'all'} onClick={() => onSelect('all')}>
      <div style={{ fontSize: 12, color: '#666' }}>Questions</div>
      <div style={{ fontSize: 24, fontWeight: 600 }}>{total}</div>
      <div style={{ fontSize: 12, color: '#666' }}>{totals.conversations ? `in ${totals.conversations} conversations` : 'in this period'}</div>
    </button>
    {shown.map((o) => <button key={o.key} type="button" style={card(selected === o.key)} aria-pressed={selected === o.key}
                              title={o.hint} onClick={() => onSelect(o.key)}>
      <div style={{ fontSize: 12, color: '#666' }}>{o.label}</div>
      <div style={{ fontSize: 24, fontWeight: 600 }}>{counts[o.key] || 0}</div>
      <div style={{ fontSize: 12, color: '#666' }}>{percent(counts[o.key] || 0, total)} of questions</div>
    </button>)}
  </div>;
}

/** Language and audience splits, shown only when there is more than one of
 *  each: a table with one row just repeats the total. */
export function Breakdowns({ totals = {}, audiences = [] }) {
  const langs = Object.entries(totals.by_lang || {});
  const groups = Object.entries(totals.by_audience || {});
  const showLangs = langs.length > 1;
  const showGroups = audiences.length > 0 && groups.some(([id]) => id !== '*');
  if (!showLangs && !showGroups) return null;
  return <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'start', margin: '8px 0 16px' }}>
    {showLangs && <table aria-label="Languages" style={{ borderCollapse: 'collapse', flex: '1 1 180px' }}>
      <thead><tr><th style={cell}>Language</th><th style={cell}>Questions</th></tr></thead>
      <tbody>{langs.map(([code, n]) => <tr key={code}><td style={cell}>{languageName(code)}</td><td style={cell}>{n}</td></tr>)}</tbody>
    </table>}
    {showGroups && <table aria-label="Audiences" style={{ borderCollapse: 'collapse', flex: '2 1 300px' }}>
      <thead><tr><th style={cell}>Audience</th><th style={cell}>Questions</th><th style={cell}>Not answered</th></tr></thead>
      <tbody>{groups.map(([id, n]) => <tr key={id}>
        <td style={cell}>{audienceLabel(audiences, id)}</td><td style={cell}>{n}</td>
        <td style={cell}>{totals.refused_by_audience?.[id] || 0} ({percent(totals.refused_by_audience?.[id] || 0, n)})</td>
      </tr>)}</tbody>
    </table>}
  </div>;
}

/** The answer the visitor was shown: citation markers link to the sources
 *  they cite, and the cited documents are listed under it. */
function AnswerCell({ item }) {
  const [open, setOpen] = React.useState(false);
  if (!item.answer) {
    return <span style={{ color: '#777' }}>{item.gate === 'error' ? 'No answer (the request failed)' : 'Not recorded'}</span>;
  }
  const sources = item.cited_sources || [];
  const sourceByN = Object.fromEntries(sources.filter((s) => s.n).map((s) => [s.n, s]));
  const long = item.answer.length > LONG_ANSWER_CHARS || item.answer.split('\n').length > 4;
  return <>
    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5, ...(long && !open ? clamped : {}) }}>
      {renderAnswer(item.answer, sourceByN)}
    </div>
    {long && <button type="button" style={linkButton} aria-expanded={open} onClick={() => setOpen((was) => !was)}>
      {open ? 'Show less' : 'Show full answer'}
    </button>}
    {sources.length > 0 && <div style={{ color: '#666', fontSize: 12, marginTop: 4 }}>
      Sources: {sources.map((s, i) => <React.Fragment key={i}>
        {i > 0 && ' · '}{s.n ? `[${s.n}] ` : ''}
        {s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer">{s.title || s.url}</a> : s.title}
      </React.Fragment>)}
    </div>}
  </>;
}

/** Every question in the period with one outcome (or all), newest first,
 *  with when it was asked and the answer given. "Show more" pages back
 *  through the whole period. */
export function QuestionLog({ corpusId, days, customerKey, adminKey, audiences = [], outcome = null,
                              showLanguage = true }) {
  const [log, setLog] = React.useState({ items: [], loading: true });
  const query = React.useRef(0); // bumped on every reload, so a late page can't land in a newer list
  const load = React.useCallback((cursor) => {
    const id = query.current;
    const params = new URLSearchParams({ corpus_id: corpusId, days, limit: QUESTIONS_PAGE });
    if (outcome) params.set('outcome', outcome);
    if (cursor) params.set('cursor', cursor);
    setLog((l) => ({ ...l, loading: true, error: null }));
    fetch(`${API_BASE_URL}/api/v1/analytics/questions?${params}`, { headers: authHeaders(adminKey, customerKey) })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((body) => {
        if (id === query.current) setLog((l) => ({ items: [...(cursor ? l.items : []), ...body.items], next: body.next_cursor, loading: false }));
      })
      .catch((e) => { if (id === query.current) setLog((l) => ({ ...l, loading: false, error: `Could not load questions: ${e.message}` })); });
  }, [corpusId, days, customerKey, adminKey, outcome]);
  React.useEffect(() => {
    setLog({ items: [], loading: true });
    load(null);
    return () => { query.current += 1; };
  }, [load]);
  const showAudience = audiences.length > 0;
  const columns = ['Date & time', 'Question', 'Answer', ...(showAudience ? ['Audience'] : []),
    ...(showLanguage ? ['Language'] : []), 'Outcome'];
  return <div>
    {log.items.length > 0 && <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead><tr>{columns.map((t) => <th style={cell} key={t}>{t}</th>)}</tr></thead>
      <tbody>{log.items.map((q) => <tr key={`${q.ts}-${q.request_id}`} style={{ verticalAlign: 'top' }}>
        <td style={{ ...cell, whiteSpace: 'nowrap' }}>{askedAt(q.ts)}</td>
        <td style={{ ...cell, minWidth: 160 }}>{q.question}</td>
        <td style={{ ...cell, minWidth: 320 }}><AnswerCell item={q} /></td>
        {showAudience && <td style={cell}>{audienceLabel(audiences, q.audience)}</td>}
        {showLanguage && <td style={cell}>{languageName(q.lang)}</td>}
        <td style={cell}>{outcomeLabel(q.gate)}</td>
      </tr>)}</tbody>
    </table></div>}
    {!log.loading && !log.error && log.items.length === 0 && <p>No questions in this period.</p>}
    {log.loading && <p role="status">Loading questions…</p>}
    {log.error && <p role="alert">{log.error}</p>}
    {log.next && !log.loading && <button type="button" style={{ marginTop: 10 }} onClick={() => load(log.next)}>Show more</button>}
  </div>;
}

const tab = (active) => ({
  padding: '8px 12px', border: 'none', borderBottom: `2px solid ${active ? '#1e5eb8' : 'transparent'}`,
  background: 'none', font: 'inherit', fontSize: 14, cursor: 'pointer', color: active ? '#111' : '#555',
  fontWeight: active ? 600 : 400,
});

export default function AnalyticsTab({ corpusId, customerKey, adminKey }) {
  const [days, setDays] = React.useState(7);
  const [outcome, setOutcome] = React.useState('all');
  const [state, setState] = React.useState({ loading: true });
  const tabsRef = React.useRef(null);
  React.useEffect(() => {
    let alive = true;
    setState({ loading: true });
    const headers = authHeaders(adminKey, customerKey);
    const read = async (path) => {
      const r = await fetch(`${API_BASE_URL}${path}`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    };
    Promise.all([
      read(`/api/v1/analytics?corpus_id=${encodeURIComponent(corpusId)}&days=${days}`),
      read(`/api/corpus/audiences?corpus_id=${encodeURIComponent(corpusId)}`),
    ]).then(([data, settings]) => { if (alive) setState({ data, audiences: settings.audiences || [] }); })
      .catch((e) => { if (alive) setState({ error: `Could not load analytics: ${e.message}` }); });
    return () => { alive = false; };
  }, [corpusId, customerKey, adminKey, days]);

  const totals = state.data?.totals || {};
  const counts = outcomeCounts(totals);
  const tabs = [{ key: 'all', label: 'All questions', n: totals.queries || 0 },
    ...OUTCOMES.filter((o) => o.key !== 'other' || counts.other).map((o) => ({ ...o, n: counts[o.key] || 0 }))];
  const openTab = (key) => {
    setOutcome(key);
    tabsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  return <section aria-label="Corpus analytics" style={{ padding: '12px 0' }}>
    <label>Period <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
      <option value={7}>Last 7 days</option><option value={14}>Last 14 days</option><option value={30}>Last 30 days</option>
    </select></label>
    {state.loading && <p role="status">Loading analytics…</p>}
    {state.error && <p role="alert">{state.error}</p>}
    {state.data && <>
      <SummaryCards totals={totals} selected={outcome} onSelect={openTab} />
      <Breakdowns totals={totals} audiences={state.audiences} />
    </>}
    <ContentGaps corpusId={corpusId} days={days} customerKey={customerKey} adminKey={adminKey} />
    {!state.loading && <section aria-label="Questions" ref={tabsRef}>
      <div role="tablist" aria-label="Questions by outcome" style={{ display: 'flex', flexWrap: 'wrap', borderBottom: '1px solid #e3e3e3', margin: '8px 0 12px' }}>
        {tabs.map((t) => <button key={t.key} type="button" role="tab" aria-selected={outcome === t.key}
                                 style={tab(outcome === t.key)} onClick={() => setOutcome(t.key)}>
          {t.label} ({t.n})
        </button>)}
      </div>
      <QuestionLog corpusId={corpusId} days={days} customerKey={customerKey} adminKey={adminKey}
                   audiences={state.audiences} outcome={outcome === 'all' ? null : outcome}
                   showLanguage={Object.keys(totals.by_lang || {}).length > 1} />
    </section>}
  </section>;
}
